/**
 * Per-host PWA manifests and service workers — doc 32 §6.3.
 *
 * WHY THIS IS ONE OF THE REAL REASONS FOR THE HOST SPLIT.
 *
 * A service worker's scope is a PATH, and only one worker may control a scope. On a
 * single origin that makes the offline contract a zero-sum fight: `public/sw.js`
 * (guest receipts + offline fallback) and `public/chow-sw.js` already contend for
 * `/`, and `hooks/usePushNotifications.ts` carries the scar tissue — registering a
 * second worker at `/` REPLACES the first and drops every cache it owned.
 *
 * So a POS cannot have its own offline behaviour on `www`. Not "should not" —
 * cannot. Four surfaces need four different contracts:
 *
 *   pos      offline-first   a till that stops when the wifi drops is not a till
 *   courier  offline-first   same, on worse connections
 *   display  offline-first   an unattended board must survive a router reboot
 *   dashboard network-first  stale analytics is a WRONG NUMBER, not a slow one
 *   m        swr             a menu should paint instantly and refresh behind it
 *   www      none            marketing needs no worker
 *
 * On its own host, each one owns `/` outright and the fight disappears. That is the
 * whole argument, and it has nothing to do with SEO.
 *
 * A SECOND, SMALLER PAYOFF: one manifest per host means a branch manager installs
 * "Menuraq POS" to a till's home screen and gets the floor — not "Menuraq", which
 * opens the marketing site. Toast and Loyverse both ship a dedicated POS app; one
 * shared manifest cannot.
 */

import { PLATFORM_HOSTS, type PlatformHost } from '@/lib/routing/hosts';

export type HostManifest = {
  name: string;
  shortName: string;
  description: string;
  /** Where the installed app opens. Host-relative — the host's root IS its mount. */
  startUrl: string;
  display: 'standalone' | 'fullscreen' | 'minimal-ui' | 'browser';
  orientation: 'any' | 'portrait' | 'landscape';
  themeColor: string;
  backgroundColor: string;
  /** The service worker file this host registers, or `null` for no worker. */
  serviceWorker: string | null;
};

/**
 * Manifests for every `installable` host.
 *
 * `scope` and `start_url` are always `/`: on a host-based deployment the host's root
 * is its mount, so a manifest scoped to `/pos` would exclude the very pages the app
 * serves. Getting this wrong produces an install that immediately navigates out of
 * its own scope and loses standalone mode — a bug that looks like "the PWA randomly
 * opens in a browser tab".
 */
export const HOST_MANIFESTS: Partial<Record<PlatformHost, HostManifest>> = {
  pos: {
    name: 'Menuraq POS',
    shortName: 'POS',
    description: 'Take orders, run the floor, and settle bills — online or off.',
    startUrl: '/',
    display: 'standalone',
    // Tills and tablets are mounted landscape. Locking it stops a mis-rotation
    // mid-service from reflowing the order pad under a server's hand.
    orientation: 'landscape',
    themeColor: '#111827',
    backgroundColor: '#111827',
    serviceWorker: '/pos-sw.js',
  },

  courier: {
    name: 'Menuraq Courier',
    shortName: 'Courier',
    description: 'Accept jobs, navigate, and confirm deliveries.',
    startUrl: '/',
    display: 'standalone',
    // A rider holds a phone one-handed.
    orientation: 'portrait',
    themeColor: '#111827',
    backgroundColor: '#111827',
    serviceWorker: '/courier-sw.js',
  },

  dashboard: {
    name: 'Menuraq Dashboard',
    shortName: 'Dashboard',
    description: 'Your numbers, your audits, your account.',
    startUrl: '/',
    display: 'standalone',
    orientation: 'any',
    themeColor: '#ffffff',
    backgroundColor: '#ffffff',
    // Network-first (public/app-sw.js): an owner reading yesterday's revenue from a
    // cache is reading a WRONG number, not a slow one. That worker caches the shell
    // and immutable build assets, and never an API response.
    serviceWorker: '/app-sw.js',
  },

  manage: {
    name: 'Menuraq Manage',
    shortName: 'Manage',
    description: 'Build the menu, run the pipeline, manage the team.',
    startUrl: '/',
    display: 'standalone',
    orientation: 'any',
    themeColor: '#ffffff',
    backgroundColor: '#ffffff',
    serviceWorker: '/app-sw.js',
  },

  operations: {
    name: 'Menuraq Operations',
    shortName: 'Operations',
    description: 'Run the floor, manage orders, handle guests.',
    startUrl: '/',
    display: 'standalone',
    orientation: 'any',
    themeColor: '#ffffff',
    backgroundColor: '#ffffff',
    serviceWorker: '/app-sw.js',
  },

  display: {
    name: 'Menuraq Display',
    shortName: 'Display',
    description: 'Order-ready and kitchen boards.',
    startUrl: '/',
    // Unattended screen: no browser chrome to accidentally navigate with.
    display: 'fullscreen',
    orientation: 'landscape',
    themeColor: '#000000',
    backgroundColor: '#000000',
    // NOT the courier worker: a board needs the KDS queue, a kiosk needs the menu,
    // and the courier worker caches neither. See public/device-sw.js.
    serviceWorker: '/device-sw.js',
  },

  kiosk: {
    name: 'Menuraq Kiosk',
    shortName: 'Kiosk',
    description: 'Self-service ordering.',
    startUrl: '/',
    display: 'fullscreen',
    orientation: 'portrait',
    themeColor: '#111827',
    backgroundColor: '#111827',
    serviceWorker: '/device-sw.js',
  },

  m: {
    name: 'Menu',
    shortName: 'Menu',
    description: 'Browse the menu and order.',
    startUrl: '/',
    display: 'standalone',
    orientation: 'portrait',
    themeColor: '#ffffff',
    backgroundColor: '#ffffff',
    // The existing guest worker: offline fallback + bounded receipt cache.
    serviceWorker: '/sw.js',
  },

  chow: {
    name: 'Chow',
    shortName: 'Chow',
    description: 'Find places to eat and track your orders.',
    startUrl: '/',
    display: 'standalone',
    orientation: 'portrait',
    themeColor: '#111827',
    backgroundColor: '#111827',
    serviceWorker: '/chow-sw.js',
  },
};

/** Icons are shared: one brand, many apps. */
export const MANIFEST_ICONS = [
  { src: '/android-chrome-192x192.png', sizes: '192x192', type: 'image/png' },
  { src: '/android-chrome-512x512.png', sizes: '512x512', type: 'image/png' },
  { src: '/apple-touch-icon.png', sizes: '180x180', type: 'image/png' },
];

export function hostManifest(host: PlatformHost): HostManifest | null {
  return HOST_MANIFESTS[host] ?? null;
}

/**
 * The service worker a host should register, or `null`.
 *
 * Returns `null` for any host that is not `live`, and that is the important part.
 * While `pos.` is `planned`, POS is served from `www`, where `/` is already
 * controlled by the guest worker — registering `pos-sw.js` there would evict
 * `sw.js` or `chow-sw.js` and silently destroy the caches they own, which is the
 * exact failure `hooks/usePushNotifications.ts` warns about. A worker may only
 * claim `/` on a host that is genuinely its own.
 */
export function serviceWorkerForHost(host: PlatformHost | null): string | null {
  if (!host) return null;
  if (PLATFORM_HOSTS[host].status !== 'live') return null;
  return hostManifest(host)?.serviceWorker ?? null;
}

/** Serialise a manifest to the W3C shape. */
export function renderManifest(manifest: HostManifest): Record<string, unknown> {
  return {
    name: manifest.name,
    short_name: manifest.shortName,
    description: manifest.description,
    start_url: manifest.startUrl,
    scope: '/',
    display: manifest.display,
    orientation: manifest.orientation,
    theme_color: manifest.themeColor,
    background_color: manifest.backgroundColor,
    lang: 'en',
    dir: 'ltr',
    icons: MANIFEST_ICONS,
    // Links into the app's scope (e.g. Star PassPRNT returning to the POS after a
    // print) open in the INSTALLED app on Android/Chromium, reusing its window,
    // instead of a new browser tab. iOS has no link capturing for home-screen apps.
    launch_handler: { client_mode: ['navigate-existing', 'auto'] },
    handle_links: 'preferred',
  };
}
